// Bridge between this site (running inside the React Native app's WebView)
// and the native app's own sign-in UI. The native app shows its
// own form (or silently replays a remembered profile) and hands the
// collected details back here to go through the site's normal
// /register/initiate call - see LoginWithoutOtp.js's handleLogin for the
// on-page equivalent this mirrors.
import { Cookies } from "react-cookie";
import callAPI from "commons/callAPI";
import config from "commons/config";
import { getUserInfo } from "commons/util/helperFunctions";
import { getAppConfig } from "commons/util/appConfigHelper";
import Util from "commons/util/util";
import { setLocalStorage } from "util/storageUtil";

export function postToNativeApp(message) {
  try {
    const data = JSON.stringify(message);
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(data);
    } else {
      window.chrome?.webview?.postMessage(data);
    }
  } catch {}
}

// Fired locally (same page) once handleIncomingSubmitLogin below finishes -
// this is what actually unblocks requestNativeLogin. The native app also
// gets a "login_result" postMessage (for its own UI state), but that's a
// one-way message TO the app, not something this page ever receives back -
// requestNativeLogin used to wait on that same channel and could never
// resolve from the real flow, only ever from its own timeout.
const LOCAL_LOGIN_RESULT_EVENT = "native-app:login_result";

// Asks the native app host to sign the user in, and resolves once
// handleIncomingSubmitLogin (below) finishes processing the app's reply.
export function requestNativeLogin(timeoutMs = 30000) {
  return new Promise((resolve) => {
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener(LOCAL_LOGIN_RESULT_EVENT, handleResult);
      clearTimeout(timer);
    };

    const handleResult = (event) => {
      cleanup();
      resolve(event.detail);
    };

    window.addEventListener(LOCAL_LOGIN_RESULT_EVENT, handleResult);

    const timer = setTimeout(() => {
      cleanup();
      resolve({ success: false, message: "Sign-in timed out. Please try again." });
    }, timeoutMs);

    postToNativeApp({ type: "request_login" });
  });
}

// Completes the login given details collected by the native app's own
// form. Always skips OTP (x-enforce-2fa: 0) - the native form doesn't have
// an OTP-entry step (yet), so this only works on sites/configs where OTP
// isn't hard-required by the backend itself.
export async function completeNativeLogin(userObj) {
  try {
    const param = btoa(JSON.stringify(userObj));
    const headers = Util.getAuthHeaders();
    headers["x-enforce-2fa"] = 0;

    const apiResponse = await callAPI.put(
      config.api.auth.initiate,
      { params: param },
      0,
      {},
      headers,
    );
    const regResponse = await apiResponse.json();

    if (!regResponse?.status?.toString()?.startsWith("20")) {
      return {
        success: false,
        message: regResponse?.message || "We couldn't sign you in. Please try again.",
      };
    }

    const userInfo = await getUserInfo("both");
    const data = userInfo?.data;

    const gwLoginData = {
      profileImageURL: "",
      email: data?.email || "",
      mobile: data?.mobile || "",
      name: data?.fullName || "",
      userId: data?.userId || "",
      // The native app is a trusted first-party client and has no OTP step,
      // so its sign-in always grants profile access.
      hasProfileAccess: true,
    };

    new Cookies().set("gwLoginData", JSON.stringify(gwLoginData), {
      path: "/",
      maxAge: 86400 * getAppConfig("LOGIN_COOKIE_EXPIRY_DAYS"),
    });

    if ((data?.segment?.code || userObj?.segment?.code) === "G") {
      setLocalStorage("guestUserData", userObj);
    }

    return { success: true };
  } catch (error) {
    console.error("Native login error:", error);
    return { success: false, message: "We couldn't sign you in. Please try again." };
  }
}

export function handleIncomingSubmitLogin(userObj) {
  completeNativeLogin(userObj).then((result) => {
    postToNativeApp({ type: "login_result", data: result });
    window.dispatchEvent(new CustomEvent(LOCAL_LOGIN_RESULT_EVENT, { detail: result }));
  });
}
